/**
 * 错误上报：配置、队列、去重、HTTP 投递
 * 使用 fetch（不走 axios 拦截器），避免上报失败再触发上报形成循环。
 * 契约对齐移动端 utils/error-report/errorConfig.js → POST /system/error/report
 */

import appErrorReport from '@/config/errorReport.js'
import { getEmbeddedVersion } from '@/utils/appVersion.js'
import { getDeviceReportInfo } from './deviceReportInfo.js'
import { readErrorReportEnabledFromLocalStorage } from './enabledFromConf.js'

const defaults = () => ({
  /** 仅当 systemConf 尚无 is_enabled 时的回退；正式以 `/system/basic/conf`.is_enabled 为准 */
  enabled: false,
  reportUrl: appErrorReport.reportUrl || '/system/error/report',
  storageKey: appErrorReport.storageKey || 'error_reports',
  enableStorage: appErrorReport.enableStorage !== false,
  enableConsole: !!appErrorReport.enableConsole,
  batchInterval: appErrorReport.batchInterval ?? 5000,
  maxBatchSize: appErrorReport.maxBatchSize ?? 10,
  dedupeTime: appErrorReport.dedupeTime ?? 300000,
})

function getBaseURL () {
  const useProxy = process.env.VUE_APP_USE_PROXY === 'true'
  return useProxy ? '/api' : (process.env.VUE_APP_BASE_API || '')
}

function getAuthToken () {
  try {
    return localStorage.getItem('token') || ''
  } catch (e) {
    return ''
  }
}

function storageGet (key) {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null || raw === '') return null
    try {
      return JSON.parse(raw)
    } catch (e) {
      return raw
    }
  } catch (e) {
    return null
  }
}

function storageSet (key, value) {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value))
  } catch (e) { /* ignore */ }
}

function storageRemove (key) {
  try {
    localStorage.removeItem(key)
  } catch (e) { /* ignore */ }
}

class ErrorConfig {
  constructor () {
    this.config = defaults()
    this.reportedErrors = new Set()
    this.pendingReports = []
    this.isReporting = false
    this.checkCount = 0
    this.lastCheckTime = 0
    this.cachedEnabled = null
    this.skipCount = 0
  }

  mergeFromAppConfig () {
    this.config = { ...this.config, ...defaults() }
  }

  /** 配置拉取后调用，清空开关缓存 */
  invalidateEnabledCache () {
    this.cachedEnabled = null
    this.checkCount = 0
    this.lastCheckTime = 0
  }

  async getConfig () {
    this.mergeFromAppConfig()
    this.cachedEnabled = this.isErrorReportEnabledSync()
    return this.cachedEnabled
  }

  isErrorReportEnabledSync () {
    this.mergeFromAppConfig()
    const fromConf = readErrorReportEnabledFromLocalStorage()
    if (fromConf !== null) return fromConf
    return !!this.config.enabled
  }

  async isErrorReportEnabled () {
    const now = Date.now()
    this.checkCount += 1
    if (now - this.lastCheckTime < 1000 && this.checkCount > 8) {
      return this.cachedEnabled !== null ? this.cachedEnabled : this.isErrorReportEnabledSync()
    }
    this.lastCheckTime = now
    this.cachedEnabled = this.isErrorReportEnabledSync()
    return this.cachedEnabled
  }

  resetCheckCount () {
    this.invalidateEnabledCache()
    this.skipCount = 0
  }

  generateErrorKey (errorInfo) {
    const msg = errorInfo.error?.message || ''
    const stack = (errorInfo.error?.stack || '').substring(0, 120)
    const page = errorInfo.page?.route || ''
    return `${errorInfo.type}_${msg}_${page}_${stack}`
  }

  hasReported (key) {
    return this.reportedErrors.has(key)
  }

  markAsReported (key) {
    this.reportedErrors.add(key)
    setTimeout(() => this.reportedErrors.delete(key), this.config.dedupeTime)
  }

  async addToQueue (errorInfo) {
    const enabled = await this.isErrorReportEnabled()
    if (!enabled) {
      this.skipCount += 1
      return
    }
    if (this.pendingReports.length >= this.config.maxBatchSize) {
      this.reportBatch()
    }
    this.pendingReports.push(errorInfo)
  }

  async reportBatch () {
    if (this.isReporting || this.pendingReports.length === 0) return
    this.isReporting = true
    const reports = [...this.pendingReports]
    this.pendingReports = []
    try {
      const result = await this.sendReports(reports)
      const ok = _isReportSuccess(result)
      if (ok && this.config.enableStorage) {
        this.clearLocalStorage(reports)
      }
    } catch (e) {
      if (this.config.enableConsole) console.warn('[error-report] batch failed', e)
    } finally {
      this.isReporting = false
    }
  }

  sendReports (reports) {
    const base = String(getBaseURL() || '').replace(/\/$/, '')
    const path = this.config.reportUrl.startsWith('/') ? this.config.reportUrl : `/${this.config.reportUrl}`
    const url = `${base}${path}`
    const reportData = {
      reports,
      timestamp: Date.now(),
      appVersion: getEmbeddedVersion() || process.env.VUE_APP_VERSION || '1.0.0',
      platform: _getPlatformInfo(),
    }
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAuthToken(),
      },
      body: JSON.stringify(reportData),
      // 不带 credentials cookie，与移动端 Bearer header 行为一致
    }).then(async (res) => {
      if (res.status !== 200) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      if (!text) return null
      try {
        return JSON.parse(text)
      } catch (e) {
        return null
      }
    })
  }

  clearLocalStorage (reports) {
    try {
      const storageKey = this.config.storageKey
      const existing = storageGet(storageKey) || []
      if (!Array.isArray(existing)) return
      const ids = new Set(reports.map((r) => r.id))
      const next = existing.filter((item) => !ids.has(item.id))
      storageSet(storageKey, next)
    } catch (e) { /* ignore */ }
  }

  getStoredErrorCount () {
    try {
      const list = storageGet(this.config.storageKey) || []
      return Array.isArray(list) ? list.length : 0
    } catch (e) {
      return 0
    }
  }

  readStoredList () {
    const list = storageGet(this.config.storageKey)
    return Array.isArray(list) ? list : []
  }

  writeStoredList (list) {
    storageSet(this.config.storageKey, list)
  }

  removeStored () {
    storageRemove(this.config.storageKey)
  }
}

function _isReportSuccess (data) {
  if (data == null) return true
  const c = data.code
  if (c === undefined) return true
  return c === 1 || c === 200 || c === 0 || data.success === true
}

function _getPlatformInfo () {
  try {
    return getDeviceReportInfo()
  } catch (e) {
    return { platform: 'web', environment: 'PC' }
  }
}

export default new ErrorConfig()
